import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import api, { setRefreshFailureCallback, startProactiveRefresh, stopProactiveRefresh, broadcastLogout } from '../utils/api';
import SessionTimeoutWarning from '../components/SessionTimeoutWarning';
import CashPinSetupGate from '../components/CashPinSetupGate';

// Permission tables + pure helpers live in `utils/permissions.js` so they can
// be unit-tested without mounting the React context. Keep this file slim —
// only the reactive state + side-effectful handlers belong here.
import {
  getRolePermissions,
  mergeStaffPermissions,
  computeCustomerPermissions,
} from '../utils/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';


const WARNING_LEAD_MS = 2 * 60 * 1000;


function decodeJwtExp(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      decodeURIComponent(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      ),
    );
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // 'staff' | 'customer' — controls which sidebar / dashboard the user sees
  const [activeView, setActiveView] = useState('staff');
  // Used on logout to nuke TanStack Query's in-memory cache so a subsequent
  // login in the same tab never sees previous user's cached data.
  const queryClient = useQueryClient();

  // ─── Session Timeout Warning state ─────────────────────────────────────
  // Modal fires when the refresh token is within WARNING_LEAD_MS of expiry.
  // Thanks to sliding refresh on the backend, any authenticated request
  // pushes the expiry out by another full window — so an active user never
  // sees this. It only shows for users idle close to the full 30-day window.
  const [timeoutWarningOpen, setTimeoutWarningOpen]       = useState(false);
  const [refreshExpiresAt,  setRefreshExpiresAt]          = useState(null);
  const warningTimerRef   = useRef(null);
  const autoLogoutTimerRef = useRef(null);

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const clearStorage = useCallback(() => {
    localStorage.removeItem('skydiving_user');
    localStorage.removeItem('customerType');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('skydiving_activeView');
    localStorage.removeItem('hideCancelledLoads')
  }, []);

  // Clear any pending session-warning timers. Called on logout / refresh
  // so we never fire a stale modal after the token has already changed.
  const clearTimeoutWarningTimers = useCallback(() => {
    const clearAny = (t) => {
      if (!t) return;
      // Chunked long-timeout handle: { id: <timerId> }
      clearTimeout(typeof t === 'object' && 'id' in t ? t.id : t);
    };
    clearAny(warningTimerRef.current);    warningTimerRef.current = null;
    clearAny(autoLogoutTimerRef.current); autoLogoutTimerRef.current = null;
    setTimeoutWarningOpen(false);
    setRefreshExpiresAt(null);
  }, []);

  // Schedule the session-timeout warning based on the current refresh token.
  // Call this after login + after every successful refresh (see cross-tab
  // BroadcastChannel handler in api.js).
  const scheduleTimeoutWarning = useCallback(() => {
    clearTimeoutWarningTimers();
    const rt = localStorage.getItem('refresh_token');
    if (!rt) return;
    const expMs = decodeJwtExp(rt);
    if (!expMs) return;
    const msUntilWarning = expMs - Date.now() - WARNING_LEAD_MS;
    const msUntilExpiry  = expMs - Date.now();
    if (msUntilExpiry <= 0) return; // already expired — let api.js handle it

    // setTimeout uses a signed 32-bit int for the delay; anything over ~24.8d
    // overflows and fires immediately. For long-lived refresh tokens (e.g. 30d)
    // we chunk the wait via a self-rescheduling stub.
    const MAX_DELAY = 2147483647;
    const setLongTimeout = (fn, delay) => {
      const tick = () => {
        const remaining = delay;
        if (remaining <= MAX_DELAY) return setTimeout(fn, Math.max(0, remaining));
        return setTimeout(() => {
          delay -= MAX_DELAY;
          handle.id = tick();
        }, MAX_DELAY);
      };
      const handle = { id: tick() };
      return handle;
    };

    // Primary timer: open the warning modal.
    const warnDelay = Math.max(0, msUntilWarning);
    warningTimerRef.current = warnDelay > MAX_DELAY
      ? setLongTimeout(() => {
          setRefreshExpiresAt(new Date(expMs));
          setTimeoutWarningOpen(true);
        }, warnDelay)
      : setTimeout(() => {
          setRefreshExpiresAt(new Date(expMs));
          setTimeoutWarningOpen(true);
        }, warnDelay);

    // Safety: even if the user closes the modal, make sure we log them out
    // exactly at expiry. The component itself also calls onLogout at 0.
    autoLogoutTimerRef.current = msUntilExpiry > MAX_DELAY
      ? setLongTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          logout();
        }, msUntilExpiry)
      : setTimeout(() => {
          // eslint-disable-next-line no-use-before-define
          logout();
        }, msUntilExpiry);
  }, [clearTimeoutWarningTimers]);

  /**
   * Given the raw user object from the backend (or localStorage), compute
   * permissions for the current activeView.
   */
  const computePermissions = useCallback((rawUser, view) => {
    if (!rawUser) return [];
    if (rawUser.email === 'admin@gmail.com') return ['all'];

    if (view === 'customer' && rawUser.hasCustomerProfile) {
      // Fun Jumper permission is only granted when the customer actually
      // classifies as a fun-jumper. Tandem and student customers previously
      // received it because the default customer perms hardcoded it — that
      // caused the "Self-Manifest" nav to show for everyone.
      return computeCustomerPermissions(rawUser.customerType);
    }

    // Staff view: prefer the backend-computed `effectiveModules` (staff
    // lane). Customer modules are intentionally NOT unioned in here —
    // they live in `rawUser.customerModules` and only apply to customer
    // view (handled in the customer branch above). Mixing them would
    // leak My Bookings / Wallet into the staff sidebar.
    //
    // Permissions also need to allow access to customer-lane modules
    // when the user has a customer profile, so an API call from the
    // customer view still passes `hasPermission`. We union those in
    // here at the permission level; the sidebar nav-builder is what
    // keeps the two lanes visually separate.
    if (rawUser.staffRoles && rawUser.staffRoles.length > 0) {
      if (rawUser.effectiveModules === 'all') return ['all'];
      const staffEff = Array.isArray(rawUser.effectiveModules)
        ? rawUser.effectiveModules
        : mergeStaffPermissions(rawUser.staffRoles, rawUser.additionalModules);
      const customerMods = Array.isArray(rawUser.customerModules) ? rawUser.customerModules : [];
      const merged = new Set(staffEff);
      for (const m of customerMods) merged.add(m);
      return Array.from(merged);
    }

    // Fallback: single role
    return getRolePermissions(rawUser.role);
  }, []);

  /**
   * Build the enriched user object that the rest of the app consumes.
   */
  const buildUser = useCallback((rawUser, view) => {
    if (!rawUser) return null;
    return {
      ...rawUser,
      // activeView makes it easy for components to check which view is active
      activeView: view,
      // For backward compat — components reading `user.role` will get the
      // effective role based on the view the user chose.
      role: view === 'customer' ? 'customer' : (rawUser.role === 'customer' && rawUser.hasStaffProfile ? (rawUser.staffRoles?.[0] || rawUser.role) : rawUser.role),
      // Keep original role untouched
      _originalRole: rawUser.role,
      // All staff roles (empty if pure customer)
      staffRoles: rawUser.staffRoles || [],
      permissions: computePermissions(rawUser, view),
    };
  }, [computePermissions]);

  // Register the refresh-failure callback so api.js can trigger logout
  useEffect(() => {
    setRefreshFailureCallback(() => {
      clearTimeoutWarningTimers();
      setUser(null);
      clearStorage();
    });
    return () => setRefreshFailureCallback(null);
  }, [clearStorage, clearTimeoutWarningTimers]);

  // Listen for silent-refresh broadcasts from api.js. After any successful
  // token rotation, reschedule the warning with the new expiry. Without
  // this, the warning timer would reflect only the original login expiry
  // and might fire even though the token was refreshed silently.
  useEffect(() => {
    let channel;
    try {
      channel = new BroadcastChannel('skydiving-auth');
      channel.onmessage = (event) => {
        if (event?.data?.type === 'tokens-updated') {
          scheduleTimeoutWarning();
        }
      };
    } catch (_) {
      /* BroadcastChannel unavailable — fine, just no cross-tab sync */
    }
    return () => { try { channel?.close(); } catch (_) {} };
  }, [scheduleTimeoutWarning]);

  // ─── Restore session from localStorage on mount ─────────────────────────
  useEffect(() => {
    const storedUser = localStorage.getItem('skydiving_user');
    const token = localStorage.getItem('access_token');
    const storedView = localStorage.getItem('skydiving_activeView');

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Determine view — respect stored preference, else infer
        let view = storedView || 'staff';
        if (!parsedUser.hasStaffProfile && parsedUser.hasCustomerProfile) view = 'customer';
        if (parsedUser.hasStaffProfile && !parsedUser.hasCustomerProfile) view = 'staff';

        setActiveView(view);
        setUser(buildUser(parsedUser, view));

        if (parsedUser.customerType) {
          localStorage.setItem('customerType', parsedUser.customerType);
        } else {
          localStorage.removeItem('customerType');
        }
        startProactiveRefresh();
        scheduleTimeoutWarning();

        // ── Refresh permissions from backend ──────────────────────────
        // localStorage's cached user shape may be stale — for instance,
        // an admin edited the StaffRoleConfig.uiVisibility tab while
        // this user was logged in, so their `additionalModules` need
        // to be recomputed. /api/auth/me does that fresh on each call.
        // Errors are swallowed: cached perms remain in force as a
        // safety floor, no spurious logout on a transient network blip.
        api.get('/auth/me')
          .then((res) => {
            const fresh = res?.data?.data;
            if (!fresh) return;
            // Re-derive view in case staff/customer profile state changed.
            let nextView = storedView || view;
            if (!fresh.hasStaffProfile && fresh.hasCustomerProfile) nextView = 'customer';
            if (fresh.hasStaffProfile && !fresh.hasCustomerProfile) nextView = 'staff';
            setUser(buildUser(fresh, nextView));
            localStorage.setItem('skydiving_user', JSON.stringify(fresh));
          })
          .catch(() => {});
      } catch (error) {
        console.error('Error parsing stored user:', error);
        clearStorage();
      }
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Login ──────────────────────────────────────────────────────────────
  const login = async (credentials) => {
    try {
      // Defense against stale session leaking into a new user's context:
      // wipe every piece of prior auth state BEFORE we write the new tokens.
      // Without this, a browser that still had a previous user's localStorage
      // entries (e.g. from a crashed tab or an un-logged-out session) could
      // mix access_token of old user with skydiving_user of the new one.
      stopProactiveRefresh();
      clearStorage();
      setUser(null);

      const response = await axios.post(`${API_URL}/api/auth/login`, {
        // Trim only (Bug 149) — no toLowerCase: the backend login lookup is
        // findOne({ email }) on the raw value, so changing case here would
        // change matching semantics. Stray whitespace from autofill/copy-paste
        // is never part of an address, so trimming is always safe.
        email: credentials.email?.trim(),
        password: credentials.password,
      });

      return adoptSession(response.data.data, credentials.preferredView);
    } catch (error) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  // ─── Adopt a session that the backend has already granted ───────────────
  // Shared by password login and group SSO. Both arrive at the same place —
  // a { user, accessToken, refreshToken } payload from THIS field's backend —
  // and everything after that point must be identical, or a person who
  // arrived via SSO gets a subtly different session: wrong starting view,
  // missing customerType, no proactive refresh.
  //
  // It does NOT authenticate. The caller must already hold a granted session.
  const adoptSession = (payload, preferredView) => {
      const { user: rawUser, accessToken, refreshToken } = payload;

      // Determine which view to start with
      let view;
      if (rawUser.hasStaffProfile && rawUser.hasCustomerProfile) {
        // Dual profile — pick based on which login page the user came from,
        // falling back to staff
        view = preferredView || 'staff';
      } else if (rawUser.hasStaffProfile) {
        view = 'staff';
      } else {
        view = 'customer';
      }

      setActiveView(view);

      const enrichedUser = buildUser(rawUser, view);
      setUser(enrichedUser);

      // Persist
      localStorage.setItem('skydiving_user', JSON.stringify(rawUser));
      localStorage.setItem('skydiving_activeView', view);
      if (rawUser.customerType) {
        localStorage.setItem('customerType', rawUser.customerType);
      } else {
        localStorage.removeItem('customerType');
      }
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('refresh_token', refreshToken);
      startProactiveRefresh();
      scheduleTimeoutWarning();

    

      // Belt-and-braces — also fire /api/auth/me right after login.
      // This guarantees the user can SEE a "fetch role permissions" call
      // in the Network tab, and also gives us a recovery path if the
      // login response was generated by an older backend that didn't
      // include `additionalModules` in the payload (cached process).
      api.get('/auth/me')
        .then((res) => {
          const fresh = res?.data?.data;
          if (!fresh) return;
          let nextView = view;
          if (!fresh.hasStaffProfile && fresh.hasCustomerProfile) nextView = 'customer';
          if (fresh.hasStaffProfile && !fresh.hasCustomerProfile) nextView = 'staff';
          setUser(buildUser(fresh, nextView));
          localStorage.setItem('skydiving_user', JSON.stringify(fresh));
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn('[auth] /me refresh after login failed', err?.message || err);
        });

      return enrichedUser;
  };

  // ─── Sign in from a group SSO hand-off ──────────────────────────────────
  // The token in the URL fragment IS the credential. This field's backend
  // verifies it and grants an ordinary session; from there it is the same
  // path a password login takes.
  const loginWithHandoff = async (handoffToken) => {
    stopProactiveRefresh();
    clearStorage();
    setUser(null);
    try {
      const response = await axios.post(`${API_URL}/api/auth/sso/handoff`, { token: handoffToken });
      // Staff: a hand-off comes from the group console, which is a staff tool.
      return adoptSession(response.data.data, 'staff');
    } catch (error) {
      throw new Error(error.response?.data?.message || 'This sign-in link is not valid.');
    }
  };

  // ─── Switch View (no re-login) ─────────────────────────────────────────
  const switchView = useCallback((targetView) => {
    const storedUser = localStorage.getItem('skydiving_user');
    if (!storedUser) return;
    const rawUser = JSON.parse(storedUser);

    // Guard: can't switch to a profile that doesn't exist
    if (targetView === 'customer' && !rawUser.hasCustomerProfile) return;
    if (targetView === 'staff' && !rawUser.hasStaffProfile) return;

    setActiveView(targetView);
    localStorage.setItem('skydiving_activeView', targetView);
    setUser(buildUser(rawUser, targetView));
  }, [buildUser]);

  // ─── Logout ─────────────────────────────────────────────────────────────
  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      stopProactiveRefresh();
      clearTimeoutWarningTimers();
      broadcastLogout();
      setUser(null);
      clearStorage();
      // Wipe TanStack Query in-memory cache so a different user logging in
      // within the same page session never sees previous user's data.
      try { queryClient.clear(); } catch (_) {}
      // Also clear any kiosk-flow sessionStorage state that might linger.
      try { sessionStorage.removeItem('kiosk_pending_booking'); } catch (_) {}
    }
  };

  const forgotPassword = async (email) => {
    try {
      // Trim only (Bug 149) — see login above for why no toLowerCase.
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email: email?.trim() });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send OTP');
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      await axios.put(`${API_URL}/api/auth/reset-password/${token}`, { newPassword });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  // ─── Refresh user from /auth/me ─────────────────────────────────────────
  // For callers that just changed profile data (name, email) and need the
  // header/nav to reflect it without a reload. Failures are swallowed —
  // the current user state stays in force.
  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      const fresh = res?.data?.data;
      if (!fresh) return null;
      setUser(buildUser(fresh, activeView));
      localStorage.setItem('skydiving_user', JSON.stringify(fresh));
      return fresh;
    } catch (_) {
      return null;
    }
  }, [buildUser, activeView]);

  const hasPermission = useCallback((module) => {
    if (!user) return false;
    if (user.permissions?.includes('all')) return true;
    return user.permissions?.includes(module) || false;
  }, [user]);

  // "Stay logged in" handler — force a refresh call which rotates the refresh
  // token and pushes the expiry another full window forward (sliding session).
  // On success, reschedule the warning for the new expiry.
  const handleExtendSession = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });
      const data = res.data?.data || {};
      if (data.accessToken)  localStorage.setItem('access_token',  data.accessToken);
      if (data.refreshToken) localStorage.setItem('refresh_token', data.refreshToken);
      // Close modal + schedule next warning from the new expiry.
      setTimeoutWarningOpen(false);
      scheduleTimeoutWarning();
    } catch (_err) {
      // Refresh failed — close the modal and let the regular flow log the user out.
      setTimeoutWarningOpen(false);
      // eslint-disable-next-line no-use-before-define
      logout();
    }
  }, [scheduleTimeoutWarning]); // eslint-disable-line react-hooks/exhaustive-deps

  // Convenience booleans
  const canSwitchView = useMemo(() => {
    if (!user) return false;
    const raw = localStorage.getItem('skydiving_user');
    if (!raw) return false;
    try {
      const r = JSON.parse(raw);
      return !!r.hasStaffProfile && !!r.hasCustomerProfile;
    } catch { return false; }
  }, [user]);

  const value = {
    user,
    loading,
    login,
    loginWithHandoff,
    logout,
    forgotPassword,
    resetPassword,
    hasPermission,
    refreshUser,
    isAuthenticated: !!user,
    // ─── New: view switching ───
    activeView,
    switchView,
    canSwitchView,
    getRolePermissions, // expose for external use
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Session timeout warning — opens shortly before the refresh token
          expires. Active users never see this because sliding refresh keeps
          pushing the expiry forward; only truly-idle users hit it. */}
      <SessionTimeoutWarning
        open={timeoutWarningOpen}
        expiresAt={refreshExpiresAt}
        onExtend={handleExtendSession}
        onLogout={() => { logout(); }}
        onOpenChange={(isOpen) => {
          // The user can dismiss the modal by closing it — keep the timers
          // running; if they're still idle at expiry, auto-logout still fires.
          if (!isOpen) setTimeoutWarningOpen(false);
        }}
      />
      {/* Cash approval PIN gate — fires for any staff user whose PIN is
          PIN_PENDING or whose admin has flagged a force-reset. Blocks all
          app interaction until they set a PIN (or log out). */}
      <CashPinSetupGate
        user={user}
        onSkipLogout={() => { logout(); }}
        onSetupComplete={(result) => {
          // Patch both the in-memory user and the persisted localStorage
          // copy so the kiosk cash flow (and any subsequent session
          // restoration) sees PIN_ACTIVE immediately, without a re-login.
          const nextStatus = {
            status: result?.status || 'PIN_ACTIVE',
            setAt: result?.setAt || new Date().toISOString(),
            forceReset: false,
          };
          try {
            const stored = localStorage.getItem('skydiving_user');
            if (stored) {
              const parsed = JSON.parse(stored);
              parsed.cashApprovalPin = nextStatus;
              localStorage.setItem('skydiving_user', JSON.stringify(parsed));
            }
          } catch (_) {
            /* if storage parse fails the in-memory patch below still works */
          }
          setUser((u) => (u ? { ...u, cashApprovalPin: nextStatus } : u));
        }}
      />
    </AuthContext.Provider>
  );
};
